import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = ({ settings, services }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', service: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Nachricht erfolgreich gesendet!');
      setSent(true);
      setForm({ name: '', email: '', phone: '', message: '', service: '' });
    } catch {
      toast.error('Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setSending(false);
    }
  };

  const hours = (settings?.opening_hours || 'Mo-Fr: 7:00 - 16:00 Uhr\nSa/So: geschlossen').split('\n');

  return (
    <section id="kontakt" className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="section-label text-[#CC2229]">Kontakt</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mt-4 tracking-tight uppercase">
            Schreiben Sie uns
          </h2>
          <p className="text-slate-500 mt-4 max-w-2xl text-base sm:text-lg">
            Wir melden uns schnellstmöglich bei Ihnen zurück. Kostenlose Beratung und unverbindliche Angebote.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            {sent ? (
              <div className="bg-white border border-green-200 p-12 text-center" data-testid="contact-success">
                <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-slate-900">Nachricht gesendet!</h3>
                <p className="text-slate-500 mt-2">Wir melden uns in Kürze bei Ihnen.</p>
                <Button
                  onClick={() => setSent(false)}
                  className="mt-6 bg-[#0077D4] hover:bg-[#005BA8] text-white rounded-none px-8 py-3 font-bold uppercase tracking-wider"
                  data-testid="contact-send-another"
                >
                  Weitere Nachricht senden
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white border border-slate-200 p-8 md:p-10 space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 font-medium text-sm">Name *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Ihr Name"
                      className="mt-2 rounded-none border-slate-300 focus:border-[#0077D4] h-12 bg-slate-50"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium text-sm">E-Mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="Ihre E-Mail"
                      className="mt-2 rounded-none border-slate-300 focus:border-[#0077D4] h-12 bg-slate-50"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium text-sm">Telefon</Label>
                    <Input
                      id="phone"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="Ihre Telefonnummer"
                      className="mt-2 rounded-none border-slate-300 focus:border-[#0077D4] h-12 bg-slate-50"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium text-sm">Leistung</Label>
                    <Select value={form.service} onValueChange={v => setForm({ ...form, service: v })}>
                      <SelectTrigger className="mt-2 rounded-none border-slate-300 h-12 bg-slate-50" data-testid="contact-service-select">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {services?.map(s => (
                          <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 font-medium text-sm">Nachricht *</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder="Beschreiben Sie Ihr Anliegen..."
                    rows={5}
                    className="mt-2 rounded-none border-slate-300 focus:border-[#0077D4] bg-slate-50 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full sm:w-auto bg-[#CC2229] hover:bg-[#a31b20] text-white rounded-none px-10 py-4 h-auto font-bold uppercase tracking-wider text-sm inline-flex items-center gap-3 transition-all duration-300 disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  <Send size={18} />
                  {sending ? 'Wird gesendet...' : 'Nachricht senden'}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-[#0077D4] text-white p-8" data-testid="contact-info">
              <h3 className="font-heading text-xl font-bold uppercase tracking-wider mb-8">Kontaktdaten</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Phone size={20} className="text-[#CC2229] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Telefon</p>
                    <a href={`tel:${settings?.phone || '0351-2038530'}`} className="text-white hover:text-[#CC2229] transition-colors font-medium">
                      {settings?.phone || '0351-2038530'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone size={20} className="text-[#CC2229] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Mobil</p>
                    <a href={`tel:${settings?.mobile || '0171-7714831'}`} className="text-white hover:text-[#CC2229] transition-colors font-medium">
                      {settings?.mobile || '0171-7714831'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail size={20} className="text-[#CC2229] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">E-Mail</p>
                    <a href={`mailto:${settings?.email || 'hke-bedachung@t-online.de'}`} className="text-white hover:text-[#CC2229] transition-colors font-medium break-all">
                      {settings?.email || 'hke-bedachung@t-online.de'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin size={20} className="text-[#CC2229] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Adresse</p>
                    <p className="text-white font-medium">{settings?.address || 'Teichplatz 7, 01259 Dresden'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock size={20} className="text-[#CC2229] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Öffnungszeiten</p>
                    {hours.map((h, i) => (
                      <p key={i} className="text-white font-medium">{h}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-8" data-testid="contact-note">
              <p className="text-slate-600 text-sm leading-relaxed">
                <span className="font-semibold text-[#0077D4]">Kostenlose Beratung</span> — Wir beraten Sie gern unverbindlich zu all Ihren Fragen rund ums Dach. Rufen Sie uns an oder nutzen Sie das Kontaktformular.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
